import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { Auth } from "aws-amplify";

import {
  IHouse,
  IHousesShotInfo,
  IResponseImage,
  TRentPeriod,
} from "interfaces/intarfaces";
import { API_HOUSES } from "utils/constants";
import { useContextHospi } from "../context/ContextHospi";
import { HouseAPI } from "API/houseApi";
import { filterHouses, IError } from "utils/helpers";
import { sortArray } from "utils/fetchMapInfo";
import { resizeImages } from "utils/resizeImages";
import { useLocation } from "react-router-dom";

type TResponseService = { status: 200; data?: string } | IError;

export interface IContextHost {
  housesShotData: IHousesShotInfo[];
  addHouseToList: (house: IHousesShotInfo) => void;
  getDataHouse: (id: string) => void;
  getHousesShortData: () => void;
  currentHouse: IHouse & TRentPeriod;
  setCurrentHouse: Dispatch<SetStateAction<IHouse & TRentPeriod>>;
  setCurrentPhotos: Dispatch<SetStateAction<{ url: string, order: number }[]>>;
  currentPhotos: string[];
  createRoom: (data: IHouse) => Promise<TResponseService>;
  updateRoom: (
    data: Partial<IHouse>,
    currentHouseId: string
  ) => Promise<TResponseService>;

  deleteRoom: (id: string) => void;
  uploadImages: (
    houseID: string,
    files: File[],
    filesForDelete?: string[],
    orderedNewImages?: { uid: string, name: string, order: number }[],
  ) => void;
  reorderImages: (
    houseID: string,
    order: { file_name: string, order: number }[],
  ) => void;
  publishRoom: (id: string) => void;
  republishRoom: (data: Partial<IHouse>) => Promise<TResponseService>;
  loading: boolean;
}

export const initialHouse: IHouse & TRentPeriod = {
  house_id: undefined,
  kind: undefined,
  title: "",
  country: "NL",
  province: "",
  postal_code: "",
  house_number: "",
  additional_house_number: "",
  street: "",
  city: "",
  bedroom_area: null,
  house_area: null,
  shared_items: [],
  private_items: [],
  description: "",
  available_from: "",
  available_to: "",
  price: null,
  additional_expenses: null,
  deposit: null,
  min_tennant_age: 0,
  max_tennant_age: 99,
  gender: "",
  spoken_languages: [],
  photos: [],
  rent_dates: [],
};

const ContextHost = createContext<IContextHost>({
  housesShotData: [],
  addHouseToList: (house: IHousesShotInfo) => console.log("Info about house"),
  getDataHouse: () => console.log("Info about house"),
  getHousesShortData: () => console.log("Info about house"),
  currentHouse: initialHouse,
  setCurrentHouse: () => console.log("Info about house"),
  currentPhotos: [],
  setCurrentPhotos: () => undefined,
  createRoom: (data: IHouse) => new Promise(() => { }),
  updateRoom: (data: Partial<IHouse>, currentHouseId: string) =>
    new Promise(() => { }),
  deleteRoom: (id: string) => console.log("Delete room"),
  uploadImages: (houseID: string, files: File[], filesForDelete?: string[]) =>
    console.log(""),
  reorderImages: (houseID: string, order: any) =>
    console.log(""),
  publishRoom: (id: string) => console.log("Publish or not room"),
  republishRoom: () => new Promise(() => { }),
  loading: false,
});

export const useContextHost = (): IContextHost => {
  return useContext<IContextHost>(ContextHost);
};

export const ContextHostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [housesShotData, setHousesShotData] = useState<IHousesShotInfo[]>([]);
  const [currentHouse, setCurrentHouse] = useState<IHouse & TRentPeriod>(
    initialHouse
  );
  const [currentPhotos, setCurrentPhotos] = useState<{ url: string, order: number }[]>([]);
  const { userProfile } = useContextHospi();
  const [loading, setLoading] = useState(false);

  const location = useLocation()

  useEffect(() => {
    if (!location.pathname.includes('room-edit')) {
      setCurrentPhotos([])
    }
  }, [location.pathname])

  const getHousesShortData = async () => {
    if (userProfile?.user_type === 'host') {
      setLoading(true);
      const { attributes } = await Auth.currentAuthenticatedUser();
      const response = await HouseAPI.get(
        API_HOUSES + `?user_id=${attributes.sub}`
      );
      setLoading(false);
      if (
        response.hasOwnProperty("status") ||
        response.hasOwnProperty("isAxiosError")
      ) {
        setHousesShotData([]);
        return false;
      } else {
        setHousesShotData(response.sort(filterHouses));
        return true;
      }
    }
  };

  const addHouseToList = (house: IHousesShotInfo) => {
    if (house.house_id === undefined) return;
    const newList = housesShotData.filter(
      (item) => item.house_id !== house.house_id
    );
    setHousesShotData([...newList, house].sort(filterHouses));
  };

  const getDataHouse = async (id: string) => {
    setCurrentPhotos([]);
    setLoading(true);
    Auth.currentAuthenticatedUser().then(({ attributes }) => {
      HouseAPI.get(API_HOUSES + `${id}`)
        .then((response) => {
          if (response.hasOwnProperty("user_id")) {
            setCurrentHouse(response);
            setCurrentPhotos(
              !!(response.photos as IResponseImage[]).length
                ? response.photos
                  .sort(sortArray)
                  .map((item: { url: string, order: number }) => ({ url: item.url, order: item.order })).sort((a: any, b: any) => a.order - b.order)
                : []
            );
          } else setCurrentHouse(initialHouse);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    });
  };

  const createRoom = async (data: IHouse): Promise<TResponseService> => {
    setLoading(true);
    try {
      const response = await HouseAPI.create(API_HOUSES, data);
      if (response.hasOwnProperty("house_id")) {
        setCurrentHouse(response);
        addHouseToList(response);
        return { status: 200, data: (response as IHouse).house_id };
      } else return response as IError;
    } catch (error) {
      return {
        status: 400,
        errorMessages: [["Something went wrong, 114", "115"]],
      } as IError;
    } finally {
      setLoading(false);
    }
  };

  const updateRoom = async (
    data: Partial<IHouse>,
    currentHouseId: string
  ): Promise<TResponseService> => {
    setLoading(true);
    try {
      const response = await HouseAPI.update(
        `${API_HOUSES}${currentHouseId}`,
        data
      );
      if (response.hasOwnProperty("house_id")) {
        setCurrentHouse(response);
        addHouseToList(response);
        return { status: 200, data: (response as IHouse).house_id };
      } else return response as IError;
    } catch (error) {
      return {
        status: 400,
        errorMessages: [["Something went wrong, 112", "113"]],
      } as IError;
    } finally {
      setLoading(false);
    }
  };

  function promiseForEach(arr: any, cb: any) {
    var i = 0;
    let date = Date.now()
    const nextPromise: any = function () {
      if (i >= arr.length) {
        // Processing finished.
        return;
      }

      // Process next function. Wrap in `Promise.resolve` in case
      // the function does not return a promise
      var newPromise = Promise.resolve(cb(arr[i], date, i + 1));
      i++;
      date = Date.now()
      // Chain to finish processing.
      return newPromise.then(nextPromise);
    };

    // Kick off the chain.
    return Promise.resolve().then(nextPromise);
  };

  const uploadImages = async (
    houseID: string,
    files: File[],
    filesForDelete?: string[],
    orderedNewImages?: { uid: string, name: string, order: number }[]
  ) => {
    setLoading(true);
    if (!!files.length) {

      const listFiles = await resizeImages(files);
      promiseForEach(listFiles, (item: any, date: number, order: number) =>
        HouseAPI.upload(
          `${API_HOUSES}${houseID}/photos`,
          item,
          date,
          orderedNewImages ? orderedNewImages.find(({ uid, name }) => orderedNewImages.hasOwnProperty("uid") ? uid === item.uid : name === item.name)!.order : order
        )
      )
    }
    if (filesForDelete && !!filesForDelete.length) {
      await HouseAPI.deleteImage(`${API_HOUSES}${currentHouse.house_id}/photos`, filesForDelete)
    }
    setLoading(false);
  };

  const reorderImages = async (houseID: string, order: { file_name: string, order: number }[]) => {
    await HouseAPI.order(`${API_HOUSES}${houseID}/set-photos-order`, order)
  }

  const deleteRoom = (id: string) => {
    setLoading(true);
    Auth.currentAuthenticatedUser().then(({ attributes }) => {
      HouseAPI.delete(API_HOUSES + `${id}`)
        .then((response) => {
          if (response.hasOwnProperty("success") && response.success)
            setHousesShotData((prev) =>
              [...prev.filter((item) => item.house_id !== id)].sort(
                filterHouses
              )
            );
          setLoading(false);
        })
        .catch(() => setLoading(false));
    });
  };

  const publishRoom = async (id: string) => {
    setLoading(true);
    try {
      const response = await HouseAPI.create(
        API_HOUSES + `${id}/toggle-publishing`,
        {} as Partial<IHouse>
      );
      if (response.hasOwnProperty("user_id"))
        setHousesShotData((prev) =>
          [...prev.filter((item) => item.house_id !== id), response].sort(
            filterHouses
          )
        );
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const republishRoom = async (
    data: Partial<IHouse>
  ): Promise<TResponseService> => {
    setLoading(true);
    try {
      await HouseAPI.create(
        `${API_HOUSES}${currentHouse.house_id}/re-publish`,
        data
      );
      addHouseToList({ ...currentHouse, ...data });
      return { status: 200 };
    } catch (error) {
      return {
        status: 400,
        errorMessages: [["Something went wrong, 110", "111"]],
      } as IError;
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContextHost.Provider
      value={{
        housesShotData,
        addHouseToList,
        getDataHouse,
        getHousesShortData,
        currentHouse,
        setCurrentHouse,
        //@ts-ignore
        currentPhotos,
        setCurrentPhotos,
        createRoom,
        updateRoom,
        deleteRoom,
        uploadImages,
        publishRoom,
        republishRoom,
        loading,
        reorderImages
      }}
    >
      {children}
    </ContextHost.Provider>
  );
};
